import { Auth } from '@aws-amplify/auth';
import { Logger } from './logger';

// Store the latest progress received from the API
let lastCompletedLesson = null;
// Flag to track if we've already fetched progress data
let hasInitialFetch = false;
// Track if a fetch is in progress to prevent duplicate calls
let isFetchInProgress = false;
// Promise for the in-progress fetch
let currentFetchPromise = null;

// Function to save user progress
export async function saveUserProgress(chapterId, lessonId) {
  try {
    // Get the current authenticated user
    const user = await Auth.currentAuthenticatedUser();
    const username = user.username;
    const token = user.signInUserSession.idToken.jwtToken;
    
    // Format the lesson identifier (c1l1, c2l2, etc.)
    const lessonKey = `c${chapterId}l${lessonId}`;
    
    // Don't save if this is already the last completed lesson
    if (lessonKey === lastCompletedLesson) {
      Logger.debug(`Progress already saved for ${lessonKey}`);
      return;
    }
    
    Logger.info(`Saving progress for user: ${username}`, { lessonKey });
    
    // Make API call to save progress
    const response = await fetch(
      `https://rbxpfff97c.execute-api.us-east-1.amazonaws.com/users/${username}/progress/save?l=${lessonKey}`,
      {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    Logger.debug('Progress saved successfully');
    // Update the last completed lesson
    lastCompletedLesson = lessonKey;
    
  } catch (error) {
    Logger.error('Error saving progress:', { error: error.message });
  }
}

// Function to fetch user progress from API
export async function fetchUserProgress(forceRefresh = false) {
  // Return cached data if available (unless force refresh is requested)
  if (hasInitialFetch && !forceRefresh) {
    Logger.debug('Using cached progress data');
    return lastCompletedLesson;
  }
  
  // If a fetch is already in progress, return the same promise
  if (isFetchInProgress) {
    Logger.debug('Progress fetch already in progress, reusing promise');
    return currentFetchPromise;
  }
  
  // Create a stack trace to identify where the call is coming from
  Logger.debug('Fetch progress called from:', { stack: new Error().stack });
  
  // Mark that a fetch is in progress
  isFetchInProgress = true;
  
  // Create a promise for the fetch
  currentFetchPromise = (async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const username = user.username;
      const token = user.signInUserSession.idToken.jwtToken;
      
      Logger.info('Fetching progress for user:', { username });
      
      const response = await fetch(
        `https://rbxpfff97c.execute-api.us-east-1.amazonaws.com/users/${username}/progress`,
        {
          method: 'GET',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Handle 404 (user has no progress yet)
      if (response.status === 404) {
        Logger.info('No progress found - new user');
        lastCompletedLesson = null;
        hasInitialFetch = true;
        return null;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      Logger.debug('User progress data:', { data });
      
      // Store the last completed lesson
      lastCompletedLesson = data.lastCompletedLesson;
      hasInitialFetch = true;
      
      return data.lastCompletedLesson;
      
    } catch (error) {
      Logger.error('Error fetching progress:', { error: error.message });
      hasInitialFetch = true; // Mark as fetched even on error to prevent excessive retries
      return null;
    } finally {
      // Reset the fetch in progress flag
      isFetchInProgress = false;
      currentFetchPromise = null;
    }
  })();
  
  return currentFetchPromise;
}

// Force refetch progress, bypassing the cache
export async function refreshUserProgress() {
  hasInitialFetch = false;
  return fetchUserProgress(true);
}

// Parse a lesson key (e.g., "c1l2") into chapter and lesson IDs
export function parseLessonKey(lessonKey) {
  if (!lessonKey) return null;
  
  const match = lessonKey.match(/c(\d+)l(\d+)/);
  if (!match) return null;
  
  return {
    chapterId: parseInt(match[1]),
    lessonId: parseInt(match[2])
  };
}

// Function to determine if a lesson should be marked as completed
export function isLessonCompleted(chapterId, lessonId, completedLessonKey) {
  if (!completedLessonKey) return false;
  
  const completed = parseLessonKey(completedLessonKey);
  if (!completed) return false;
  
  return (
    chapterId < completed.chapterId || 
    (chapterId === completed.chapterId && lessonId <= completed.lessonId)
  );
}

// Get the currently stored lastCompletedLesson without making an API call
export function getLastCompletedLesson() {
  return lastCompletedLesson;
}

// Function to check if a given lesson (chapter/lesson ID) is further than the latest saved progress
export function isLessonFurtherThanSaved(chapterId, lessonId) {
  // Get the last completed lesson from our cache
  if (!lastCompletedLesson) return true;
  
  const saved = parseLessonKey(lastCompletedLesson);
  if (!saved) return true;
  
  // Compare the given lesson with the saved progress
  return (
    chapterId > saved.chapterId || 
    (chapterId === saved.chapterId && lessonId > saved.lessonId)
  );
} 